import { useState, useRef, useEffect, useCallback } from 'react';
import { ExpressionInput } from 'calc-controls';
import { useStore } from '@/store';
import type { StudyArm, ConcentrationTimePoint } from '@bioavail/shared-types';
import { Toggle } from '@/components/ui/TextInput';
import { NumberInput } from '@/components/ui/NumberInput';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { CsvImportDialog } from './CsvImportDialog';

/** Per-arm tabular data entry (P1.5.5, F-023/F-059/F-008). */
export function DataEntryTable({ arm }: { arm: StudyArm }) {
  const addDataPoint = useStore((s) => s.addDataPoint);
  const removeDataPoint = useStore((s) => s.removeDataPoint);
  const setDataPoints = useStore((s) => s.setDataPoints);
  const setDirectAuc = useStore((s) => s.setDirectAuc);
  const [importOpen, setImportOpen] = useState(false);
  const [deletedPoint, setDeletedPoint] = useState<{ point: ConcentrationTimePoint; index: number } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rowRefs = useRef<Map<number, HTMLInputElement>>(new Map());

  const focusNextRow = useCallback((currentIndex: number) => {
    const nextIndex = currentIndex + 1;
    const nextInput = rowRefs.current.get(nextIndex);
    if (nextInput) {
      nextInput.focus();
    }
  }, []);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    };
  }, []);

  function handleDelete(index: number) {
    const point = arm.dataPoints[index];
    if (!point) return;
    removeDataPoint(arm.id, index);
    setDeletedPoint({ point, index });
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => setDeletedPoint(null), 5000);
  }

  function handleUndo() {
    if (!deletedPoint) return;
    const next = [...arm.dataPoints];
    next.splice(deletedPoint.index, 0, deletedPoint.point);
    setDataPoints(arm.id, next);
    setDeletedPoint(null);
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }

  if (arm.usesDirectAuc) {
    return (
      <div>
        <Toggle
          label="Use direct AUC entry"
          checked
          onChange={() => setDirectAuc(arm.id, undefined)}
          hint="Switch back to concentration–time entry."
        />
        <NumberInput
          label="Direct AUC"
          value={arm.directAuc ?? ''}
          min={0}
          step="any"
          onChange={(v) => setDirectAuc(arm.id, v)}
          error={arm.directAuc !== undefined && arm.directAuc <= 0 ? 'Direct AUC must be > 0.' : undefined}
          hint={`Pre-computed area in ${arm.concentrationUnit}·${arm.timeUnit}. Bypasses trapezoidal integration and λ_z.`}
        />
      </div>
    );
  }

  const points = arm.dataPoints;

  return (
    <div>
      <Toggle
        label="Use direct AUC entry"
        checked={false}
        onChange={() => setDirectAuc(arm.id, 0)}
        hint="Enter a pre-computed AUC instead of concentration–time points."
      />

      {points.length === 0 ? (
        <p className="empty-state">No data points yet. Add rows manually or import a CSV/TSV file.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time ({arm.timeUnit})</th>
                <th>Conc. ({arm.concentrationUnit})</th>
                <th>
                  BLQ
                  <Tooltip label="Below limit of quantification" learnMore={{ label: 'See regulatory guidance', screen: 'reference' }}>
                    Below limit of quantification — M1 treats as 0 during integration.
                  </Tooltip>
                </th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {points.map((p, i) => (
                <tr key={i}>
                  <td
                    ref={(el) => {
                      const input = el?.querySelector('input');
                      if (input) rowRefs.current.set(i, input);
                      else rowRefs.current.delete(i);
                    }}
                    onBlur={() => {
                      const sorted = [...points].sort((a, b) => a.time - b.time);
                      setDataPoints(arm.id, sorted);
                    }}
                  >
                    <ExpressionInput
                      value={p.time}
                      onChange={(v) => {
                        const next = [...points];
                        next[i] = { ...next[i]!, time: v };
                        setDataPoints(arm.id, next);
                      }}
                      min={0}
                      decimals={4}
                      inline
                      aria-label={`Time for row ${i + 1}`}
                    />
                  </td>
                  <td
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        focusNextRow(i);
                      }
                    }}
                  >
                    <ExpressionInput
                      value={p.concentration}
                      onChange={(v) => {
                        const next = [...points];
                        next[i] = { ...next[i]!, concentration: v };
                        setDataPoints(arm.id, next);
                      }}
                      min={0}
                      decimals={4}
                      inline
                      aria-label={`Concentration for row ${i + 1}`}
                    />
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      aria-label={`BLQ for row ${i + 1}`}
                      checked={p.isBlq}
                      onChange={(e) => {
                        const next = [...points];
                        next[i] = { ...next[i]!, isBlq: e.target.checked };
                        setDataPoints(arm.id, next);
                      }}
                    />
                  </td>
                  <td className="data-table-row-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      aria-label={`Remove row ${i + 1}`}
                      onClick={() => handleDelete(i)}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deletedPoint ? (
        <div className="undo-bar" role="status">
          <span className="undo-bar-message">Row {deletedPoint.index + 1} deleted.</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={handleUndo}>
            Undo
          </button>
        </div>
      ) : null}

      <div className="flex-row-gap-2 mt-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => addDataPoint(arm.id, { time: 0, concentration: 0, isBlq: false })}
        >
          Add row
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
          Import CSV
        </Button>
      </div>

      <CsvImportDialog open={importOpen} onClose={() => setImportOpen(false)} arm={arm} />
    </div>
  );
}
